import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { customersApi as C, CUST_STATUS_STYLE } from "@/lib/customers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Loader2 } from "lucide-react";

export default function Customers() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ categories: [], statuses: [] });
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([C.stats(), C.list({ search: search || undefined, status, category })]);
      setStats(s); setRows(list);
    } finally { setLoading(false); }
  };
  useEffect(() => { C.meta().then(setMeta); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, category]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Customers</h2>
          <p className="mt-1 text-sm text-slate-500">The single source of truth for all customer data.</p>
        </div>
        {can("customers", "create") && (
          <Button onClick={() => navigate("/customers/new")} data-testid="new-customer-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
            <Plus className="mr-2 h-4 w-4" />New Customer
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4" data-testid="cust-stat-total"><p className="text-xs font-medium uppercase tracking-wider text-slate-500">Total</p><p className="mt-1 font-heading text-2xl font-bold text-slate-900">{stats.total ?? 0}</p></Card>
        <Card className="p-4" data-testid="cust-stat-active"><p className="text-xs font-medium uppercase tracking-wider text-slate-500">Active</p><p className="mt-1 font-heading text-2xl font-bold text-emerald-600">{stats.active ?? 0}</p></Card>
        <Card className="p-4" data-testid="cust-stat-inactive"><p className="text-xs font-medium uppercase tracking-wider text-slate-500">Inactive</p><p className="mt-1 font-heading text-2xl font-bold text-rose-600">{stats.inactive ?? 0}</p></Card>
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search company / code / GSTIN" className="pl-9" data-testid="cust-search-input" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-44" data-testid="cust-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="cust-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Code</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Company</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">GSTIN</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Category</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">City</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Credit limit</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/customers/${r.id}`)} data-testid={`customer-row-${r.customer_code}`}>
                    <TableCell className="font-medium text-slate-800">{r.customer_code}</TableCell>
                    <TableCell className="text-slate-700">{r.company_name}</TableCell>
                    <TableCell className="text-slate-500">{r.gstin || "—"}</TableCell>
                    <TableCell className="text-slate-600">{r.category}</TableCell>
                    <TableCell className="text-slate-600">{r.billing_address?.city || "—"}</TableCell>
                    <TableCell className="tabular-nums text-slate-600">{r.credit_limit ? `₹${r.credit_limit.toLocaleString("en-IN")}` : "—"}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${CUST_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-12 text-center text-slate-400" data-testid="cust-empty">No customers yet. Create your first customer.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
